import { useEffect, useState } from 'react';
import { registerModalBackHandler } from '../../lib/native';
import { Avatar, PhotoGrid, PhotoLightbox, SectionDivider } from '../design-system';
import type { HostProfile } from '../../types';

interface HostProfilePreviewModalProps {
  hostName: string;
  /** The host's own profile photo, so the preview matches what travelers see. */
  hostAvatar?: string | null;
  profile: HostProfile;
  /** Resolved city names for the host's current (unsaved) city selection. */
  cities: string[];
  onClose: () => void;
}

/**
 * Shows a host roughly what a traveler sees on HostProfileView — using the
 * host's in-progress edits (not yet saved), so they can check how a change
 * will look before hitting Save. Deliberately a simplified read-only render
 * rather than reusing HostProfileView directly, since that component fetches
 * match/traveler-specific data (match note, reasons, handbook status) that
 * doesn't exist yet at the profile-editing stage.
 */
export function HostProfilePreviewModal({ hostName, hostAvatar, profile, cities, onClose }: HostProfilePreviewModalProps) {
  const firstName = hostName.split(' ')[0] || 'You';
  const city = cities[0] ?? profile.city ?? '';
  const otherCities = cities.filter((c) => c !== city);
  const moodboard = profile.moodboard_images ?? [];
  const photos = moodboard.filter(Boolean);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const specialties = profile.specialties ?? [];

  // Android hardware back button closes this preview instead of navigating
  // the route underneath it.
  // Skipped while the photo viewer is open so back closes only that.
  useEffect(() => {
    if (lightboxIndex !== null) return;
    return registerModalBackHandler(onClose);
  }, [onClose, lightboxIndex]);

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-app-bg md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-2xl max-h-dvh-92 overflow-y-auto pb-safe-or-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-app-bg/95 backdrop-blur border-b border-app-border px-5 py-3.5 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-app-fg">Traveler preview</p>
            <p className="text-xs text-app-muted">This is roughly how travelers see your profile once matched.</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="w-8 h-8 rounded-full bg-app-card border border-app-border text-app-muted flex items-center justify-center hover:text-app-fg shrink-0 sk-hit"
          >
            <i className="ti ti-x text-base" />
          </button>
        </div>

        <div className="px-5 pt-6 pb-10">
          {/* Hero */}
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5" style={{ marginBottom: 10 }}>
            <Avatar name={hostName} src={hostAvatar ?? undefined} size={72} className="sm:mt-2" />
            <div className="min-w-0">
              <h1
                className="font-serif text-app-fg"
                style={{ fontSize: 'clamp(28px, 8vw, 36px)', fontWeight: 400, lineHeight: 1.06, letterSpacing: '-.015em', textWrap: 'pretty' }}
              >
                {firstName} has you<br />in {city || '…'}
              </h1>
              {otherCities.length > 0 && (
                <p className="text-app-muted text-xs mt-2">Also hosts in {otherCities.join(' · ')}</p>
              )}
              {profile.headline && (
                <p className="text-sm text-app-fg-2 mt-2">{profile.headline}</p>
              )}
            </div>
          </div>

          {/* Video */}
          <div style={{ marginTop: 32 }}>
            {profile.video_url ? (
              <div className="rounded-2xl overflow-hidden bg-black border border-app-border min-h-[200px]">
                <video src={profile.video_url} controls playsInline preload="metadata" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-3 min-h-[160px]"
                style={{ border: '1px solid var(--line)', borderRadius: 16, background: 'var(--surface)' }}
              >
                <div className="text-center px-4">
                  <div className="font-serif text-sm text-app-fg">Intro coming soon</div>
                  <div className="text-caption text-app-muted mt-1">{firstName} hasn't uploaded a video yet</div>
                </div>
              </div>
            )}
          </div>

          {/* In host's words */}
          {(profile.bio || specialties.length > 0) && (
            <div style={{ marginTop: 36 }}>
              <SectionDivider icon="quote" label={`In ${firstName}'s words`} style={{ marginBottom: 16 }} />
              {profile.bio ? (
                <div className="flex gap-5 items-start">
                  <div className="w-0.5 shrink-0 rounded-sm self-stretch" style={{ background: 'var(--line)' }} />
                  <p className="font-serif text-app-fg" style={{ fontSize: 'var(--fs-body)', fontWeight: 400, lineHeight: 1.6, textWrap: 'pretty', whiteSpace: 'pre-wrap' }}>
                    {profile.bio}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-app-muted italic">No bio written yet.</p>
              )}
              {specialties.length > 0 && (
                <div className="flex flex-wrap gap-1.5" style={{ marginTop: 18 }}>
                  {specialties.map((s) => (
                    <span key={s} className="text-app-fg-2" style={{ fontSize: 'var(--fs-caption2)', fontWeight: 500, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '2px 8px' }}>
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Feed */}
          <div style={{ marginTop: 36 }}>
            <PhotoGrid
              images={moodboard}
              count={9}
              columns={3}
              gap={3}
              radius={3}
              alt={`${firstName}'s photo`}
              onCellClick={(i) => { if (moodboard[i]) setLightboxIndex(moodboard.slice(0, i).filter(Boolean).length); }}
            />
          </div>
        </div>
      </div>
    </div>
    {lightboxIndex !== null && (
      <PhotoLightbox
        images={photos}
        startIndex={lightboxIndex}
        alt={`${firstName}'s photo`}
        onClose={() => setLightboxIndex(null)}
      />
    )}
    </>
  );
}

export default HostProfilePreviewModal;
